/**
 * ServiceDependencyGraph — §14 static service dependency graph (SVG).
 *
 * No external chart deps (plan §14 component mapping). A fixed
 * topology: LexRuntime → LexServer → {MongoDB, Redis, Clerk}. Nodes
 * are coloured by the live status the parent already computes
 * (`statusOf`), so the graph is a topology view of the same signal the
 * cards show — not a second probe.
 *
 * Azure-only edge metrics (latency percentiles, queue depth, I/O) are
 * v1.1 (Phase B data source); this is the dependency map only.
 */

type Status = "ok" | "degraded" | "unknown";

interface NodeSpec {
  id: string;
  label: string;
  x: number;
  y: number;
}

// Fixed layout — LexRuntime feeds LexServer which fans out to its
// three backing services.
const NODES: NodeSpec[] = [
  { id: "lexruntime", label: "LexRuntime", x: 70, y: 110 },
  { id: "lexserver", label: "LexServer", x: 250, y: 110 },
  { id: "mongodb", label: "MongoDB", x: 440, y: 40 },
  { id: "redis", label: "Redis", x: 440, y: 110 },
  { id: "clerk", label: "Clerk", x: 440, y: 180 },
];

const EDGES: Array<[string, string]> = [
  ["lexruntime", "lexserver"],
  ["lexserver", "mongodb"],
  ["lexserver", "redis"],
  ["lexserver", "clerk"],
];

const FILL: Record<Status, string> = {
  ok: "#16a34a",
  degraded: "#dc2626",
  unknown: "#94a3b8",
};

export interface ServiceDependencyGraphProps {
  statusOf: (id: string) => Status;
}

export function ServiceDependencyGraph({ statusOf }: ServiceDependencyGraphProps) {
  const nodeById = (id: string) => NODES.find((n) => n.id === id)!;
  const NODE_W = 110;
  const NODE_H = 36;

  return (
    <svg
      viewBox="0 0 560 230"
      className="w-full max-w-2xl"
      role="img"
      aria-label="Service dependency graph"
      data-testid="service-dependency-graph"
    >
      {/* Edges */}
      {EDGES.map(([from, to]) => {
        const a = nodeById(from);
        const b = nodeById(to);
        const x1 = a.x + NODE_W;
        const y1 = a.y + NODE_H / 2;
        const x2 = b.x;
        const y2 = b.y + NODE_H / 2;
        // Degraded if either endpoint is degraded.
        const sa = statusOf(from);
        const sb = statusOf(to);
        const edgeColor =
          sa === "degraded" || sb === "degraded"
            ? FILL.degraded
            : sa === "unknown" || sb === "unknown"
              ? FILL.unknown
              : FILL.ok;
        return (
          <line
            key={`${from}-${to}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={edgeColor}
            strokeWidth={1.5}
            strokeDasharray={edgeColor === FILL.unknown ? "4 3" : undefined}
            data-testid={`dep-edge-${from}-${to}`}
          />
        );
      })}

      {/* Nodes */}
      {NODES.map((n) => {
        const s = statusOf(n.id);
        return (
          <g key={n.id} data-testid={`dep-node-${n.id}`} data-status={s}>
            <rect
              x={n.x}
              y={n.y}
              width={NODE_W}
              height={NODE_H}
              rx={6}
              fill="white"
              stroke={FILL[s]}
              strokeWidth={2}
              className="dark:fill-slate-900"
            />
            <circle cx={n.x + 14} cy={n.y + NODE_H / 2} r={4} fill={FILL[s]} />
            <text
              x={n.x + 26}
              y={n.y + NODE_H / 2 + 4}
              fontSize={12}
              className="fill-slate-700 dark:fill-slate-200"
            >
              {n.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
